import { User, ShieldAlert, Car, Euro, ClipboardCheck, PenTool, Check, AlertTriangle } from 'lucide-react';
import type { StepNumber } from '@/lib/mietwagen-wizard-validation';

const STEPS: { label: string; Icon: React.ElementType }[] = [
  { label: 'Kunde & Fahrer', Icon: User },
  { label: 'Schaden & Vers.', Icon: ShieldAlert },
  { label: 'Fahrzeuge', Icon: Car },
  { label: 'Konditionen', Icon: Euro },
  { label: 'Übergabe', Icon: ClipboardCheck },
  { label: 'Unterschrift', Icon: PenTool },
];

const ACTIVE_COLOR = '#E85D04';

interface WizardProgressBarProps {
  currentStep: StepNumber;
  validSteps: Set<StepNumber>;
  onStepClick: (step: StepNumber) => void;
}

export default function WizardProgressBar({ currentStep, validSteps, onStepClick }: WizardProgressBarProps) {
  return (
    <div className="sticky top-0 z-30 bg-white border-b shadow-sm">
      <div className="flex items-stretch overflow-x-auto scrollbar-none">
        {STEPS.map(({ label, Icon }, index) => {
          const step = (index + 1) as StepNumber;
          const isActive = step === currentStep;
          const isValid = validSteps.has(step);
          // step 5 is always valid (all optional) — don't show warning
          const showWarning = !isValid && step !== 5 && step < currentStep;

          return (
            <button
              key={step}
              onClick={() => onStepClick(step)}
              className={[
                'flex flex-1 min-w-0 items-center justify-center gap-1.5 px-2 py-3 transition-all text-left',
                'focus:outline-none focus-visible:ring-2 focus-visible:ring-orange-400',
                isActive
                  ? 'border-b-2 border-orange-500 bg-orange-50'
                  : 'border-b-2 border-transparent hover:bg-gray-50',
              ].join(' ')}
              style={isActive ? { borderBottomColor: ACTIVE_COLOR } : undefined}
              aria-current={isActive ? 'step' : undefined}
            >
              {/* Icon + state indicator */}
              <div className="relative flex-shrink-0">
                <Icon
                  className="w-4 h-4"
                  style={{ color: isActive ? ACTIVE_COLOR : isValid ? '#16a34a' : '#9ca3af' }}
                />
                {isValid && (
                  <span className="absolute -top-1.5 -right-1.5 w-3 h-3 rounded-full bg-green-500 flex items-center justify-center">
                    <Check className="w-2 h-2 text-white" />
                  </span>
                )}
                {showWarning && (
                  <span className="absolute -top-1.5 -right-1.5 w-3 h-3 rounded-full bg-amber-400 flex items-center justify-center">
                    <AlertTriangle className="w-2 h-2 text-white" />
                  </span>
                )}
              </div>

              {/* Step number always visible; label only on md+ */}
              <span
                className={[
                  'text-xs font-medium truncate',
                  isActive ? 'text-orange-600' : isValid ? 'text-green-700' : 'text-gray-400',
                ].join(' ')}
              >
                <span className="md:hidden">{step}</span>
                <span className="hidden md:inline">{label}</span>
              </span>
            </button>
          );
        })}
      </div>
    </div>
  );
}
